/**
 * Hooks for cloud agent conversations and tasks (async polling model).
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiRequest } from "@lato/client/lib/apiClient";
import { connectTabKeyForStart } from "@/features/integrations/connectTab";
import { supabase } from "@lato/client/lib/supabase";
import { queryClient } from "@/lib/queryClient";
import { create } from "zustand";

// ── Types ─────────────────────────────────────────────────────────────────

export interface HeartbeatItem {
  id: string;
  description: string;
  prompt?: string | null;
  interval_minutes?: number | null;
}

export interface HeartbeatConfig {
  enabled: boolean;
  interval_minutes: number;
  items: HeartbeatItem[];
  isolated_session?: boolean;
  active_hours?: { start: string; end: string } | null;
  active_days?: number[] | null;
}

export interface CronJob {
  id: string;
  agent_id: string;
  name: string;
  cron_expr: string | null;
  interval_seconds: number | null;
  at: string | null;
  prompt: string;
  session_mode: string;
  transport: string | null;
  skills: string[];
  status: string;
  next_run_at: string | null;
  last_run_at: string | null;
  run_count: number;
  created_at: string;
}

export interface CloudAgent {
  id: string;
  name: string;
  avatar_url: string | null;
  soul: string | null;
  general_instructions: string | null;
  user_context: string | null;
  timezone: string;
  heartbeat_config: HeartbeatConfig | null;
}

export interface ConversationEntry {
  conversation_id: string;
  agent_id: string;
  title: string | null;
  preview: string | null;
  status: string;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface CloudMessage {
  role: "user" | "assistant";
  content: string;
}

interface OrgMembership {
  id: string;
  name: string;
  slug: string;
  role: string;
}

export interface MeUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  avatar_url: string | null;
  initials: string;
}

export interface MeResponse {
  user: MeUser;
  // All memberships (multi-org: a person can belong to several funds).
  // Empty → the invite wall.
  organizations: OrgMembership[];
  // The acting org (the validated org_id token claim); null if none.
  organization: OrgMembership | null;
  // Whether the caller's org may use the studies module. Open to every org in preview, so true
  // whenever there's an acting org. Drives nav visibility / route guards.
  studies_enabled: boolean;
  // Whether the caller's org has full studies access (launch + live actions) vs. preview-only.
  // Drives the Preview badge + disabling the Launch control; the backend gate is authoritative.
  studies_full_access: boolean;
  // Whether the caller's org may use the sourcing module (Exa-powered search + lists).
  // Drives nav visibility / route guards; the backend gate is authoritative.
  sourcing_enabled: boolean;
  // Whether the caller is a platform (super) admin — reveals the Admin sidebar section + staff
  // affordances. The backend `PlatformAdmin` dependency is authoritative.
  is_platform_admin: boolean;
}

interface OrgDetail {
  id: string;
  name: string;
  slug: string;
  domain: string | null;
  homepage: string | null;
  about: string | null;
  avatar_url: string | null;
  role: string;
}

interface OrgMember {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  initials: string;
  role: string;
}

interface OrgDetailResponse {
  organization: OrgDetail;
  members: OrgMember[];
}

// ── Query keys ────────────────────────────────────────────────────────────

export const cloudKeys = {
  me: ["cloud", "me"] as const,
  agents: ["cloud", "agents"] as const,
  conversations: ["conversations"] as const,
  messages: (conversationId: string) => ["cloud", "messages", conversationId] as const,
  affinityStatus: ["integrations", "affinity"] as const,
  microsoftStatus: ["integrations", "microsoft"] as const,
  googleStatus: ["integrations", "google"] as const,
  harmonicStatus: ["integrations", "harmonic"] as const,
  mcpConnection: (provider: string) => ["integrations", "mcp", provider] as const,
  slackStatus: ["slack", "status"] as const,
  customIntegrations: (orgId: string) => ["proxy", "integrations", orgId] as const,
  allowedDomains: (orgId: string) => ["proxy", "domains", orgId] as const,
  orgDetail: (orgId: string) => ["cloud", "org", orgId] as const,
  orgInvites: (orgId: string) => ["cloud", "org", orgId, "invites"] as const,
  invitePreview: (token: string) => ["cloud", "invite", token] as const,
  myMember: (orgId: string) => ["cloud", "org", orgId, "member", "me"] as const,
  cronJobs: (agentId: string) => ["scheduler", "jobs", agentId] as const,
  workflows: (agentId: string) => ["workflows", agentId] as const,
};

// ── My membership (profile + status) ─────────────────────────────────────

export interface MyMember {
  profile_md: string | null;
  profile_linkedin: string | null;
  profile_status: string | null;
}

export function useMyMember(orgId: string) {
  return useQuery({
    queryKey: cloudKeys.myMember(orgId),
    queryFn: async () => {
      const res = await apiRequest(`/cloud/organizations/${orgId}/members/me`);
      if (!res.ok) throw new Error("Failed to fetch member profile");
      return (await res.json()) as MyMember;
    },
    enabled: !!orgId,
  });
}

// ── Unread conversations store ───────────────────────────────────────────

export const useUnreadConversations = create<{
  ids: Set<string>;
  viewing: string | null;
  add: (id: string) => void;
  remove: (id: string) => void;
  setViewing: (id: string | null) => void;
}>((set, get) => ({
  ids: new Set(),
  viewing: null,
  add: (id) => {
    if (get().viewing === id) return;
    set((s) => ({ ids: new Set(s.ids).add(id) }));
  },
  remove: (id) =>
    set((s) => {
      const next = new Set(s.ids);
      next.delete(id);
      return { ids: next };
    }),
  setViewing: (id) => {
    if (id && get().ids.has(id)) {
      const next = new Set(get().ids);
      next.delete(id);
      set({ viewing: id, ids: next });
    } else {
      set({ viewing: id });
    }
  },
}));

// Conversation IDs we know are pending (seeded by send mutation, updated by polls)
const pendingConversationIds = new Set<string>();

// First-message text for new conversations awaiting an auto-generated title. Populated when a
// new conversation is seeded; consumed once its first run completes (see useConversations).
const pendingTitleMessages = new Map<string, string>();

/**
 * Auto-name a new conversation from its first message, then patch the title into the Recents
 * cache. Mirrors the Excel add-in's post-first-run title generation; best-effort and silent on
 * failure (the server falls back to "New chat", and the row keeps showing its preview).
 */
/**
 * Generate a title for a new conversation now that its first run has finished, if one is still
 * awaited. Safe to call repeatedly (consumes the stashed message; no-ops once titled). This is
 * the reliable trigger — driven by the chat run-finished signal — that complements the Recents
 * poll's completion detection.
 */
export function requestConversationTitle(conversationId: string): void {
  const message = pendingTitleMessages.get(conversationId);
  if (!message) return;
  pendingTitleMessages.delete(conversationId);
  const convs = queryClient.getQueryData<ConversationEntry[]>(cloudKeys.conversations);
  if (convs?.find((c) => c.conversation_id === conversationId)?.title) return;
  void generateConversationTitle(conversationId, message);
}

async function generateConversationTitle(conversationId: string, message: string): Promise<void> {
  try {
    const res = await apiRequest(`/conversations/${conversationId}/generate-title`, {
      method: "POST",
      body: JSON.stringify({ message }),
    });
    if (!res.ok) return;
    const data = (await res.json()) as { conversation_id: string; title: string | null };
    if (!data.title) return;
    queryClient.setQueryData<ConversationEntry[]>(cloudKeys.conversations, (old) =>
      (old ?? []).map((c) =>
        c.conversation_id === conversationId ? { ...c, title: data.title } : c
      )
    );
  } catch {
    // best-effort: titling must never disrupt the chat experience
  }
}

// ── Hooks ─────────────────────────────────────────────────────────────────

async function fetchAgents(orgId: string): Promise<CloudAgent[]> {
  // Scope to the acting org explicitly so the response always matches the
  // org-scoped cache key below — even in the brief window after an org switch
  // where the refreshed token claim is still catching up. Without it a stale
  // cross-org list could leak between orgs.
  const res = await apiRequest(`/cloud/agents?org_id=${orgId}`);
  if (!res.ok) throw new Error("Failed to fetch agents");
  const data = (await res.json()) as { agents: CloudAgent[] };
  return data.agents;
}

export function useCloudAgents(): UseQueryResult<CloudAgent[]> {
  // Org-scoped cache key: switching orgs is a clean cache miss (data → undefined)
  // rather than the previous org's agents lingering. That staleness would let the
  // silent seed (useEnsureAgent) mistake the new org for "already has an agent"
  // and create it without the browser timezone. `cloudKeys.agents` stays the
  // invalidation prefix, so `meta.invalidates` still matches every org's entry.
  const { data: me } = useMe();
  const orgId = me?.organization?.id;
  return useQuery({
    queryKey: [...cloudKeys.agents, orgId ?? "none"],
    queryFn: () => fetchAgents(orgId as string),
    enabled: !!orgId,
  });
}

async function fetchMe(speculative = false) {
  const res = await apiRequest("/users/me", { speculative });
  if (!res.ok) throw new Error("Failed to fetch profile");
  return (await res.json()) as MeResponse;
}

export const meQueryOptions = {
  queryKey: cloudKeys.me,
  queryFn: () => fetchMe(),
  // Changes reach this through the events that cause them: sign-in clears the cache, an org switch
  // and a token for another org invalidate it. The age limit only bounds a role or entitlement
  // changed elsewhere, and is the life of the token the answer was read with.
  staleTime: 60 * 60 * 1000,
} as const;

/**
 * Ask the server even if `me` is cached, sharing the cache and any request already in flight:
 * `/users/me` is what activates a pending membership, so sign-in and invite acceptance need it to
 * have run. The caller handles failure, so it isn't retried here.
 */
export function fetchFreshMe(client: QueryClient): Promise<MeResponse> {
  return client.fetchQuery({ ...meQueryOptions, staleTime: 0, retry: false });
}

/**
 * `meQueryOptions` variant for route loaders: when `preload` is set, a hover-triggered
 * cache miss fetches speculatively so it can't sign the user out. Same query key, so
 * preload and real navigation share the one `["cloud","me"]` cache entry.
 */
export function meQueryOptionsFor(preload: boolean) {
  return preload ? { ...meQueryOptions, queryFn: () => fetchMe(true) } : meQueryOptions;
}

export function useMe() {
  return useQuery(meQueryOptions);
}

export function useOrgDetail(orgId: string) {
  return useQuery({
    queryKey: cloudKeys.orgDetail(orgId),
    queryFn: async () => {
      const res = await apiRequest(`/cloud/organizations/${orgId}`);
      if (!res.ok) throw new Error("Failed to fetch organization");
      return (await res.json()) as OrgDetailResponse;
    },
    enabled: !!orgId,
  });
}

export function useUpdateMe() {
  return useMutation({
    mutationFn: async (body: {
      first_name?: string | null;
      last_name?: string | null;
      avatar_url?: string | null;
    }) => {
      const res = await apiRequest(`/users/me`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Failed to update profile");
      return (await res.json()) as MeUser;
    },
    meta: { invalidates: [cloudKeys.me] },
  });
}

export function useSwitchOrg() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orgId: string) => {
      const res = await apiRequest(`/users/me/current-org`, {
        method: "PUT",
        body: JSON.stringify({ org_id: orgId }),
      });
      if (!res.ok) throw new Error("Failed to switch workspace");
      // The acting org lives in the access token (org_id claim) — reissue it
      // so the next requests act in the new org.
      await supabase.auth.refreshSession();
    },
    onSuccess: () => {
      // Everything org-scoped changes wholesale: agents, conversations, me.
      void queryClient.invalidateQueries();
    },
  });
}

export function useUpdateOrg(orgId: string) {
  return useMutation({
    mutationFn: async (body: {
      name?: string;
      homepage?: string | null;
      about?: string | null;
      avatar_url?: string | null;
    }) => {
      const res = await apiRequest(`/cloud/organizations/${orgId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Failed to update workspace");
      return (await res.json()) as OrgDetail;
    },
    meta: { invalidates: [cloudKeys.orgDetail(orgId)] },
  });
}

// ── Invites ──────────────────────────────────────────────────────────────

interface OrgInvite {
  id: string;
  email: string;
  role: string;
  created_at: string;
}

interface OrgInvitesResponse {
  invites: OrgInvite[];
}

export function useOrgInvites(orgId: string) {
  return useQuery({
    queryKey: cloudKeys.orgInvites(orgId),
    queryFn: async () => {
      const res = await apiRequest(`/cloud/organizations/${orgId}/invites`);
      if (!res.ok) throw new Error("Failed to fetch invites");
      return (await res.json()) as OrgInvitesResponse;
    },
    enabled: !!orgId,
  });
}

export function useCreateInvite(orgId: string) {
  return useMutation({
    mutationFn: async (body: { email: string; role?: string; seed?: Record<string, string> }) => {
      const res = await apiRequest(`/cloud/organizations/${orgId}/invites`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error((data as { detail?: string } | null)?.detail ?? "Failed to create invite");
      }
      return (await res.json()) as OrgInvite;
    },
    meta: { invalidates: [cloudKeys.orgInvites(orgId)] },
  });
}

async function expectOk(res: Response, fallback: string): Promise<void> {
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error((data as { detail?: string } | null)?.detail ?? fallback);
  }
}

export function useRevokeInvite(orgId: string) {
  return useMutation({
    mutationFn: async (inviteId: string) => {
      const res = await apiRequest(`/cloud/organizations/${orgId}/invites/${inviteId}`, {
        method: "DELETE",
      });
      await expectOk(res, "Failed to revoke invite");
    },
    meta: { invalidates: [cloudKeys.orgInvites(orgId)] },
  });
}

export function useResendInvite(orgId: string) {
  return useMutation({
    mutationFn: async (inviteId: string) => {
      const res = await apiRequest(`/cloud/organizations/${orgId}/invites/${inviteId}/resend`, {
        method: "POST",
      });
      await expectOk(res, "Failed to resend invite");
    },
    meta: { invalidates: [cloudKeys.orgInvites(orgId)] },
  });
}

export function useDeactivateMember(orgId: string) {
  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await apiRequest(`/cloud/organizations/${orgId}/members/${userId}`, {
        method: "DELETE",
      });
      await expectOk(res, "Failed to remove member");
    },
    meta: { invalidates: [cloudKeys.orgDetail(orgId)] },
  });
}

export function useSetMemberRole(orgId: string) {
  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const res = await apiRequest(`/cloud/organizations/${orgId}/members/${userId}`, {
        method: "PATCH",
        body: JSON.stringify({ role }),
      });
      await expectOk(res, "Failed to change role");
    },
    meta: { invalidates: [cloudKeys.orgDetail(orgId)] },
  });
}

export interface InvitePreview {
  org_name: string | null;
  inviter: string | null;
  role: string;
  expired: boolean;
  accepted: boolean;
}

/** Public preview for the /invite/:token landing page (no auth required). */
export function useInvitePreview(token: string) {
  return useQuery({
    queryKey: cloudKeys.invitePreview(token),
    queryFn: async () => {
      const res = await apiRequest(`/cloud/invites/${token}`);
      if (res.status === 404) return null;
      if (!res.ok) throw new Error("Failed to load invite");
      return (await res.json()) as InvitePreview;
    },
    enabled: !!token,
    retry: false,
  });
}

/** Error carrying the HTTP status so the invite page can map 403/404/409/410 to walls. */
export class AcceptInviteError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

/** Redeem an email-bound invite for the signed-in user. */
export function useAcceptInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (token: string) => {
      // An OAuth callback may be the first request for this company's session.
      // Activate its pending/JIT membership before the strict invite endpoint.
      await fetchFreshMe(queryClient);
      const res = await apiRequest(`/cloud/invites/${token}/accept`, { method: "POST" });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new AcceptInviteError(
          (data as { detail?: string } | null)?.detail ?? "Failed to accept invite",
          res.status
        );
      }
      const accepted = (await res.json()) as { org_id: string };
      // Membership activation alone must not land an invitee in an older
      // workspace. This door also demands the invited company's specific SSO.
      const switched = await apiRequest("/users/me/current-org", {
        method: "PUT",
        body: JSON.stringify({ org_id: accepted.org_id }),
      });
      if (!switched.ok)
        throw new Error(
          "Unable to enter the invited workspace. Please contact your administrator."
        );
      const { error } = await supabase.auth.refreshSession();
      if (error) throw error;
      return accepted;
    },
    meta: { invalidates: [cloudKeys.me, cloudKeys.agents] },
  });
}

export function useCreateAgent() {
  return useMutation({
    mutationFn: async (body: {
      name: string;
      org_id: string;
      avatar_url?: string;
      timezone?: string;
    }) => {
      const res = await apiRequest("/cloud/agents", {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Failed to create agent");
      return (await res.json()) as CloudAgent;
    },
    // The silent seed (useEnsureAgent) fires once per mount; retry once so a
    // transient failure doesn't leave the org's agent unseeded for the session.
    retry: 1,
    meta: { invalidates: [cloudKeys.agents] },
  });
}

export function useUpdateAgent() {
  return useMutation({
    mutationFn: async ({
      agentId,
      ...body
    }: {
      agentId: string;
      name?: string;
      avatar_url?: string | null;
      soul?: string | null;
      general_instructions?: string | null;
      user_context?: string | null;
      timezone?: string;
      heartbeat_config?: HeartbeatConfig | null;
    }) => {
      const res = await apiRequest(`/cloud/agents/${agentId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Failed to update agent");
      return (await res.json()) as CloudAgent;
    },
    meta: { invalidates: [cloudKeys.agents] },
  });
}

export function useSendWelcome() {
  return useMutation({
    mutationFn: async (agentId: string) => {
      const res = await apiRequest(`/cloud/agents/${agentId}/welcome`, { method: "POST" });
      if (!res.ok) throw new Error("Failed to send welcome");
      return (await res.json()) as { status: string };
    },
  });
}

// ── Scheduler hooks ──────────────────────────────────────────────────────

export function useCronJobs(agentId: string) {
  return useQuery({
    queryKey: cloudKeys.cronJobs(agentId),
    enabled: !!agentId,
    queryFn: async () => {
      const res = await apiRequest(`/scheduler/jobs?agent_id=${agentId}`);
      if (!res.ok) throw new Error("Failed to fetch cron jobs");
      const data = (await res.json()) as { jobs: CronJob[] };
      return data.jobs;
    },
  });
}

export function useCreateCronJob() {
  return useMutation({
    mutationFn: async (body: {
      agent_id: string;
      name: string;
      prompt: string;
      cron_expr?: string;
      interval_seconds?: number;
      at?: string;
      session_mode?: string;
      transport?: string;
    }) => {
      const res = await apiRequest("/scheduler/jobs", {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Failed to create cron job");
      return (await res.json()) as CronJob;
    },
    meta: {
      invalidates: ({ variables }) => {
        const { agent_id } = variables as { agent_id: string };
        return [cloudKeys.cronJobs(agent_id)];
      },
    },
  });
}

export function useUpdateCronJob(agentId: string) {
  return useMutation({
    mutationFn: async ({
      jobId,
      ...body
    }: {
      jobId: string;
      name?: string;
      prompt?: string;
      cron_expr?: string;
      status?: string;
      transport?: string | null;
    }) => {
      const res = await apiRequest(`/scheduler/jobs/${jobId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Failed to update cron job");
      return (await res.json()) as CronJob;
    },
    meta: { invalidates: [cloudKeys.cronJobs(agentId)] },
  });
}

export function useDeleteCronJob(agentId: string) {
  return useMutation({
    mutationFn: async (jobId: string) => {
      const res = await apiRequest(`/scheduler/jobs/${jobId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete cron job");
    },
    meta: { invalidates: [cloudKeys.cronJobs(agentId)] },
  });
}

function hasPending(conversations: ConversationEntry[] | undefined): boolean {
  return conversations?.some((c) => c.status === "queued" || c.status === "running") ?? false;
}

/**
 * Optimistically reflect a just-started web run in the Recents list, so a new conversation
 * shows in the sidebar immediately (and an existing one bumps to the top as pending). This
 * also flips `useConversations` into its polling state (`hasPending`), so the entry's
 * status/preview fill in from the server. `conversationId` is the browser-minted id (= PK,
 * Option B). The run itself is started by the chat module's transport, not here.
 */
export function seedPendingConversation(
  orgId: string,
  conversationId: string,
  previewText?: string
): void {
  pendingConversationIds.add(conversationId);
  if (previewText) pendingTitleMessages.set(conversationId, previewText);
  const now = new Date().toISOString();
  queryClient.setQueryData<ConversationEntry[]>(cloudKeys.conversations, (old) => {
    const existing = old?.find((c) => c.conversation_id === conversationId);
    if (existing) {
      return (old ?? []).map((c) =>
        c.conversation_id === conversationId
          ? { ...c, status: "queued", error_message: null, updated_at: now }
          : c
      );
    }
    return [
      {
        conversation_id: conversationId,
        agent_id: orgId,
        title: null,
        preview: previewText?.slice(0, 120) ?? null,
        status: "queued",
        error_message: null,
        created_at: now,
        updated_at: now,
      },
      ...(old ?? []),
    ];
  });
}

export function useRenameConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ conversationId, title }: { conversationId: string; title: string }) => {
      const res = await apiRequest(`/conversations/${conversationId}`, {
        method: "PATCH",
        body: JSON.stringify({ title }),
      });
      if (!res.ok) throw new Error("Failed to rename conversation");
    },
    onMutate: ({ conversationId, title }) => {
      const previous = queryClient.getQueryData<ConversationEntry[]>(cloudKeys.conversations);
      queryClient.setQueryData<ConversationEntry[]>(cloudKeys.conversations, (old) =>
        (old ?? []).map((c) => (c.conversation_id === conversationId ? { ...c, title } : c))
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(cloudKeys.conversations, context.previous);
    },
    meta: { invalidates: [cloudKeys.conversations] },
  });
}

export function useArchiveConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (conversationId: string) => {
      const res = await apiRequest(`/conversations/${conversationId}`, {
        method: "PATCH",
        body: JSON.stringify({ archived: true }),
      });
      if (!res.ok) throw new Error("Failed to archive conversation");
    },
    onMutate: (conversationId) => {
      // Optimistically drop it from Recents (and stop tracking it as pending) — remember what
      // we cleared so a failed archive can restore it, not just the list snapshot: otherwise the
      // settled invalidation's refetch drops an in-flight conversation from the sidebar for good
      // (useConversations only re-attaches a preview-less row while its id is still pending).
      const wasPending = pendingConversationIds.has(conversationId);
      const pendingTitleMessage = pendingTitleMessages.get(conversationId);
      pendingConversationIds.delete(conversationId);
      pendingTitleMessages.delete(conversationId);
      const previous = queryClient.getQueryData<ConversationEntry[]>(cloudKeys.conversations);
      queryClient.setQueryData<ConversationEntry[]>(cloudKeys.conversations, (old) =>
        (old ?? []).filter((c) => c.conversation_id !== conversationId)
      );
      return { previous, wasPending, pendingTitleMessage };
    },
    onError: (_err, conversationId, context) => {
      if (context?.previous) queryClient.setQueryData(cloudKeys.conversations, context.previous);
      if (context?.wasPending) pendingConversationIds.add(conversationId);
      if (context?.pendingTitleMessage !== undefined) {
        pendingTitleMessages.set(conversationId, context.pendingTitleMessage);
      }
    },
    meta: { invalidates: [cloudKeys.conversations] },
  });
}

export function useConversations() {
  return useQuery({
    queryKey: cloudKeys.conversations,
    staleTime: 0,
    queryFn: async () => {
      const res = await apiRequest("/conversations/recent?transport=web");
      if (!res.ok) throw new Error("Failed to fetch conversations");
      const data = (await res.json()) as { conversations: ConversationEntry[] };

      // Preserve optimistic previews for pending conversations whose task
      // hasn't been created yet (server returns preview: null)
      const cached = queryClient.getQueryData<ConversationEntry[]>(cloudKeys.conversations);
      const cachedById = new Map((cached ?? []).map((c) => [c.conversation_id, c]));
      if (cached) {
        for (const c of data.conversations) {
          if (!c.preview && pendingConversationIds.has(c.conversation_id)) {
            const prev = cachedById.get(c.conversation_id);
            if (prev?.preview) c.preview = prev.preview;
          }
        }
      }

      // Keep a just-seeded conversation visible until the server lists it. A new run's row +
      // task may not exist server-side yet (e.g. a slow deep-research run), so without this the
      // optimistic entry would vanish from Recents until the agent answers.
      const serverIds = new Set(data.conversations.map((c) => c.conversation_id));
      const optimistic = (cached ?? []).filter(
        (c) => pendingConversationIds.has(c.conversation_id) && !serverIds.has(c.conversation_id)
      );
      if (optimistic.length) data.conversations = [...optimistic, ...data.conversations];

      // Detect completed conversations that were pending → mark unread
      const { add } = useUnreadConversations.getState();
      for (const c of data.conversations) {
        if (c.status === "queued" || c.status === "running") {
          pendingConversationIds.add(c.conversation_id);
        } else if (c.status === "completed" && pendingConversationIds.has(c.conversation_id)) {
          pendingConversationIds.delete(c.conversation_id);
          add(c.conversation_id);
          // Fallback to the run-finished trigger: name a new conversation once it completes.
          requestConversationTitle(c.conversation_id);
        }
      }

      return data.conversations;
    },
    refetchInterval: (query) => (hasPending(query.state.data) ? 3000 : false),
  });
}

// ── Integrations ─────────────────────────────────────────────────────────

interface IntegrationStatus {
  connected: boolean;
}

interface MicrosoftStatus {
  connected: boolean;
  features: string[];
  /** Microsoft rejected the grant: kept, but unusable until the user reconnects. */
  needs_reauth?: boolean;
}

export function useAffinityStatus(orgId: string) {
  return useQuery({
    queryKey: [...cloudKeys.affinityStatus, orgId],
    queryFn: async () => {
      const res = await apiRequest(`/integrations/affinity?org_id=${orgId}`);
      if (!res.ok) throw new Error("Failed to fetch status");
      return (await res.json()) as IntegrationStatus;
    },
  });
}

export function useConnectAffinity() {
  return useMutation({
    mutationFn: async ({ apiKey, orgId }: { apiKey: string; orgId: string }) => {
      const res = await apiRequest(`/integrations/affinity?org_id=${orgId}`, {
        method: "PUT",
        body: JSON.stringify({ api_key: apiKey }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { detail?: string } | null;
        throw new Error(body?.detail ?? "Failed to connect");
      }
      return (await res.json()) as IntegrationStatus;
    },
    meta: { invalidates: [cloudKeys.affinityStatus] },
  });
}

export function useDisconnectAffinity() {
  return useMutation({
    mutationFn: async (orgId: string) => {
      const res = await apiRequest(`/integrations/affinity?org_id=${orgId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to disconnect");
      return (await res.json()) as IntegrationStatus;
    },
    meta: { invalidates: [cloudKeys.affinityStatus] },
  });
}

// ── Harmonic ─────────────────────────────────────────────────────────────

export function useHarmonicStatus(orgId: string) {
  return useQuery({
    queryKey: [...cloudKeys.harmonicStatus, orgId],
    queryFn: async () => {
      const res = await apiRequest(`/integrations/harmonic?org_id=${orgId}`);
      if (!res.ok) throw new Error("Failed to fetch status");
      return (await res.json()) as IntegrationStatus;
    },
  });
}

export function useConnectHarmonic() {
  return useMutation({
    mutationFn: async ({ apiKey, orgId }: { apiKey: string; orgId: string }) => {
      const res = await apiRequest(`/integrations/harmonic?org_id=${orgId}`, {
        method: "PUT",
        body: JSON.stringify({ api_key: apiKey }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { detail?: string } | null;
        throw new Error(body?.detail ?? "Failed to connect");
      }
      return (await res.json()) as IntegrationStatus;
    },
    meta: { invalidates: [cloudKeys.harmonicStatus] },
  });
}

export function useDisconnectHarmonic() {
  return useMutation({
    mutationFn: async (orgId: string) => {
      const res = await apiRequest(`/integrations/harmonic?org_id=${orgId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to disconnect");
      return (await res.json()) as IntegrationStatus;
    },
    meta: { invalidates: [cloudKeys.harmonicStatus] },
  });
}

// ── Microsoft 365 ────────────────────────────────────────────────────────

export function useMicrosoftStatus(orgId: string) {
  return useQuery({
    queryKey: [...cloudKeys.microsoftStatus, orgId],
    queryFn: async () => {
      const res = await apiRequest(`/integrations/microsoft/status?org_id=${orgId}`);
      if (!res.ok) throw new Error("Failed to fetch status");
      return (await res.json()) as MicrosoftStatus;
    },
  });
}

export function useMicrosoftAuthorize() {
  return useMutation({
    mutationFn: async ({
      features,
      orgId,
      replace,
      returnTo,
    }: {
      features: string[];
      orgId: string;
      replace?: boolean;
      returnTo?: string;
    }) => {
      const params = new URLSearchParams();
      for (const f of features) params.append("features", f);
      params.append("org_id", orgId);
      if (replace) params.append("replace", "true");
      if (returnTo) params.append("return_to", returnTo);
      params.append("tab_key", connectTabKeyForStart());
      const res = await apiRequest(`/integrations/microsoft/authorize?${params.toString()}`);
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { detail?: string } | null;
        throw new Error(body?.detail ?? "Failed to start authorization");
      }
      return (await res.json()) as { url: string };
    },
  });
}

export function useDisconnectMicrosoft() {
  return useMutation({
    mutationFn: async (orgId: string) => {
      const res = await apiRequest(`/integrations/microsoft?org_id=${orgId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to disconnect");
      return (await res.json()) as MicrosoftStatus;
    },
    meta: { invalidates: [cloudKeys.microsoftStatus] },
  });
}

// ── Google Workspace ─────────────────────────────────────────────────────

interface GoogleStatus {
  connected: boolean;
  features: string[];
  granted_scopes: string;
  /** Google rejected the grant: kept, but unusable until the user reconnects. */
  needs_reauth?: boolean;
}

export function useGoogleStatus(orgId: string) {
  return useQuery({
    queryKey: [...cloudKeys.googleStatus, orgId],
    queryFn: async () => {
      const res = await apiRequest(`/integrations/google/status?org_id=${orgId}`);
      if (!res.ok) throw new Error("Failed to fetch status");
      return (await res.json()) as GoogleStatus;
    },
  });
}

/**
 * Resolve which high-level capabilities are connected (mail, calendar) across
 * providers, so callers never reimplement the gmail/outlook → mail mapping.
 */
export function useConnectedCapabilities(orgId: string) {
  const google = useGoogleStatus(orgId);
  const microsoft = useMicrosoftStatus(orgId);
  // A connection that needs reconnecting grants nothing until it is reconnected.
  const g = google.data?.needs_reauth ? [] : (google.data?.features ?? []);
  const m = microsoft.data?.needs_reauth ? [] : (microsoft.data?.features ?? []);
  return {
    mail: g.includes("gmail") || m.includes("outlook"),
    calendar: g.includes("calendar") || m.includes("calendar"),
    isLoading: google.isLoading || microsoft.isLoading,
  };
}

export function useGoogleAuthorize() {
  return useMutation({
    mutationFn: async ({
      features,
      orgId,
      replace,
      returnTo,
    }: {
      features: string[];
      orgId: string;
      replace?: boolean;
      returnTo?: string;
    }) => {
      const params = new URLSearchParams();
      for (const f of features) params.append("features", f);
      params.append("org_id", orgId);
      if (replace) params.append("replace", "true");
      if (returnTo) params.append("return_to", returnTo);
      params.append("tab_key", connectTabKeyForStart());
      const res = await apiRequest(`/integrations/google/authorize?${params.toString()}`);
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { detail?: string } | null;
        throw new Error(body?.detail ?? "Failed to start authorization");
      }
      return (await res.json()) as { url: string };
    },
  });
}

export function useDisconnectGoogle() {
  return useMutation({
    mutationFn: async (orgId: string) => {
      const res = await apiRequest(`/integrations/google?org_id=${orgId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to disconnect");
      return (await res.json()) as GoogleStatus;
    },
    meta: { invalidates: [cloudKeys.googleStatus] },
  });
}

// ── Hosted MCP servers (Granola, Notion, ...) ─────────────────────────────────

interface McpConnection {
  connected: boolean;
  /** Whether users can connect: the server has a client with the provider, or can register one. */
  available: boolean;
  /** The provider rejected the grant: kept, but unusable until the user reconnects. */
  needs_reauth: boolean;
}

export function useMcpConnection(provider: string, orgId: string) {
  return useQuery({
    queryKey: [...cloudKeys.mcpConnection(provider), orgId],
    queryFn: async () => {
      const res = await apiRequest(`/organizations/${orgId}/integrations/${provider}`);
      if (!res.ok) throw new Error("Failed to fetch status");
      return (await res.json()) as McpConnection;
    },
    enabled: !!orgId,
  });
}

export function useMcpAuthorize(provider: string) {
  return useMutation({
    mutationFn: async ({ orgId, returnTo }: { orgId: string; returnTo?: string }) => {
      const res = await apiRequest(`/organizations/${orgId}/integrations/${provider}/authorize`, {
        method: "POST",
        body: JSON.stringify({ return_to: returnTo ?? "/", tab_key: connectTabKeyForStart() }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { detail?: string } | null;
        throw new Error(body?.detail ?? "Failed to start authorization");
      }
      return (await res.json()) as { url: string };
    },
  });
}

export function useDisconnectMcp(provider: string) {
  return useMutation({
    mutationFn: async (orgId: string) => {
      const res = await apiRequest(`/organizations/${orgId}/integrations/${provider}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to disconnect");
      return (await res.json()) as McpConnection;
    },
    meta: { invalidates: [cloudKeys.mcpConnection(provider)] },
  });
}

// ── Slack ────────────────────────────────────────────────────────────

export interface SlackUnifiedStatus {
  bot_installed: boolean;
  scopes_current: boolean;
  user_connected: boolean;
  user_features: string[];
  team_id: string | null;
}

export function useSlackStatus(orgId: string) {
  return useQuery({
    queryKey: [...cloudKeys.slackStatus, orgId],
    queryFn: async () => {
      const res = await apiRequest(`/slack/status?org_id=${orgId}`);
      if (!res.ok) throw new Error("Failed to fetch Slack status");
      return (await res.json()) as SlackUnifiedStatus;
    },
  });
}

export function useSlackConnect() {
  return useMutation({
    mutationFn: async ({
      features,
      orgId,
      returnTo,
    }: {
      features: string[];
      orgId: string;
      returnTo?: string;
    }) => {
      const params = new URLSearchParams();
      for (const f of features) params.append("features", f);
      params.append("org_id", orgId);
      if (returnTo) params.append("return_to", returnTo);
      const res = await apiRequest(`/slack/connect?${params.toString()}`);
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { detail?: string } | null;
        throw new Error(body?.detail ?? "Failed to start Slack connection");
      }
      return (await res.json()) as { url: string };
    },
  });
}

export function useDisconnectSlack() {
  return useMutation({
    mutationFn: async (orgId: string) => {
      const res = await apiRequest(`/integrations/slack?org_id=${orgId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to disconnect");
    },
    meta: { invalidates: [cloudKeys.slackStatus] },
  });
}

// ── Custom Integrations + Allowed Domains ────────────────────────────────

interface CustomIntegration {
  id: string;
  host: string;
  auth_type: string;
  display_name: string | null;
}

interface AllowedDomain {
  id: string;
  domain: string;
}

export function useCustomIntegrations(orgId: string) {
  return useQuery({
    queryKey: cloudKeys.customIntegrations(orgId),
    queryFn: async () => {
      const res = await apiRequest(`/proxy/integrations?org_id=${orgId}`);
      if (!res.ok) throw new Error("Failed to fetch custom integrations");
      return (await res.json()) as { integrations: CustomIntegration[] };
    },
    enabled: !!orgId,
  });
}

export function useAddCustomIntegration(orgId: string) {
  return useMutation({
    mutationFn: async (body: {
      host: string;
      auth_type: string;
      api_key: string;
      header_name?: string;
      display_name?: string;
    }) => {
      const res = await apiRequest(`/proxy/integrations?org_id=${orgId}`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { detail?: string } | null;
        throw new Error(data?.detail ?? "Failed to add integration");
      }
      return (await res.json()) as { connected: boolean; host: string };
    },
    meta: { invalidates: [cloudKeys.customIntegrations(orgId)] },
  });
}

export function useDeleteCustomIntegration(orgId: string) {
  return useMutation({
    mutationFn: async (integrationId: string) => {
      const res = await apiRequest(`/proxy/integrations/${integrationId}?org_id=${orgId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete integration");
      return (await res.json()) as { connected: boolean };
    },
    meta: { invalidates: [cloudKeys.customIntegrations(orgId)] },
  });
}

export function useAllowedDomains(orgId: string) {
  return useQuery({
    queryKey: cloudKeys.allowedDomains(orgId),
    queryFn: async () => {
      const res = await apiRequest(`/proxy/domains?org_id=${orgId}`);
      if (!res.ok) throw new Error("Failed to fetch allowed domains");
      return (await res.json()) as { domains: AllowedDomain[] };
    },
    enabled: !!orgId,
  });
}

export function useAddAllowedDomain(orgId: string) {
  return useMutation({
    mutationFn: async (domain: string) => {
      const res = await apiRequest(`/proxy/domains?org_id=${orgId}`, {
        method: "POST",
        body: JSON.stringify({ domain }),
      });
      if (!res.ok) throw new Error("Failed to add domain");
      return (await res.json()) as { id: string; domain: string };
    },
    meta: { invalidates: [cloudKeys.allowedDomains(orgId)] },
  });
}

export function useDeleteAllowedDomain(orgId: string) {
  return useMutation({
    mutationFn: async (domainId: string) => {
      const res = await apiRequest(`/proxy/domains/${domainId}?org_id=${orgId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete domain");
      return (await res.json()) as { deleted: boolean };
    },
    meta: { invalidates: [cloudKeys.allowedDomains(orgId)] },
  });
}
